import { useEffect, useState } from 'react';
import { readResetTokenFromUrl } from './api/client';
import { AuthProvider, type SessionUser, useAuth } from './auth/AuthProvider';
import { Spinner } from './components/ui';
import { navigate, usePathname } from './router';
import { type Acesso, resolveRoute } from './routes';
import { AppLayout } from './screens/AppLayout';
import { EsqueciSenha } from './screens/EsqueciSenha';
import { Login } from './screens/Login';
import { NaoEncontrada } from './screens/NaoEncontrada';
import { OnboardingPublico } from './screens/OnboardingPublico';
import { PrivacidadePublica } from './screens/PrivacidadePublica';
import { RedefinirSenha } from './screens/RedefinirSenha';
import styles from './screens/screens.module.css';
import { ThemeProvider } from './theme/ThemeProvider';

/** Limpa o ?token= da URL após o fluxo de redefinição (sem recarregar a página). */
function clearTokenFromUrl() {
  if (typeof window === 'undefined') return;
  window.history.replaceState(null, '', window.location.pathname);
}

/** Rotas pré-sessão: login, esqueci-senha e redefinir-senha (via ?token=). */
function UnauthRoutes() {
  const tokenInicial =
    typeof window !== 'undefined' ? readResetTokenFromUrl(window.location.search) : null;
  const [tela, setTela] = useState<'login' | 'esqueci' | 'redefinir'>(
    tokenInicial ? 'redefinir' : 'login',
  );

  if (tela === 'redefinir' && tokenInicial) {
    return (
      <RedefinirSenha
        token={tokenInicial}
        onDone={() => {
          clearTokenFromUrl();
          setTela('login');
        }}
      />
    );
  }
  if (tela === 'esqueci') return <EsqueciSenha onBack={() => setTela('login')} />;
  return <Login onForgot={() => setTela('esqueci')} />;
}

/**
 * Tela da URL atual para o papel da sessão. Rota de outro papel volta ao Início
 * (replace, sem montar a tela — logo sem chamar a API que negaria o acesso).
 */
function TenantApp({ user, pathname }: { user: SessionUser; pathname: string }) {
  const acesso: Acesso = {
    papel: user.papel,
    modulos: new Set(user.modulos),
    suspenso: user.suspenso,
  };
  const resolution = resolveRoute(pathname, acesso);
  const forbidden = resolution.kind === 'forbidden';

  useEffect(() => {
    if (forbidden) navigate('/', { replace: true });
  }, [forbidden]);

  if (resolution.kind === 'forbidden') return null;
  if (resolution.kind === 'not_found') {
    return (
      <AppLayout pathname={pathname} title="Página não encontrada">
        <NaoEncontrada />
      </AppLayout>
    );
  }
  return (
    <AppLayout pathname={pathname} title={resolution.route.title}>
      {resolution.route.render(acesso)}
    </AppLayout>
  );
}

function Routed() {
  const { loading, user } = useAuth();
  const pathname = usePathname();

  if (loading) {
    return (
      <div className={styles.centerScreen}>
        <Spinner />
      </div>
    );
  }
  if (!user) return <UnauthRoutes />;
  return <TenantApp user={user} pathname={pathname} />;
}

export function App() {
  // Rota pública de Política de Privacidade (#74): renderiza sem sessão/tenant.
  if (typeof window !== 'undefined' && window.location.pathname === '/privacidade') {
    return (
      <ThemeProvider>
        <PrivacidadePublica />
      </ThemeProvider>
    );
  }
  // Rota pública de cadastro self-service (#80): sem sessão/tenant.
  if (typeof window !== 'undefined' && window.location.pathname === '/cadastro') {
    return (
      <ThemeProvider>
        <OnboardingPublico />
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routed />
      </AuthProvider>
    </ThemeProvider>
  );
}
